import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const ReservationModal = ({ isOpen, onClose }) => {
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    guests: '',
    time: ''
  });
  
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);

  const allTimeSlots = [
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', 
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', 
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', 
    '20:00', '20:30', '21:00'
  ];

  useEffect(() => {
    if (!date) {
      setAvailableTimeSlots([]);
      return;
    }

    const selectedDate = new Date(date);
    const dayOfWeek = selectedDate.getDay(); 

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      setAvailableTimeSlots([]);
      if (formData.time) {
        setFormData(prev => ({ ...prev, time: '' }));
      }
      return;
    }

    // Filter time slots to only allow up to 15:30
    const filteredSlots = allTimeSlots.filter(time => {
      const [hours, minutes] = time.split(':').map(Number);
      const timeInMinutes = hours * 60 + minutes;
      const limitInMinutes = 15 * 60 + 30; // 15:30
      return timeInMinutes <= limitInMinutes;
    });

    setAvailableTimeSlots(filteredSlots);

    if (formData.time && !filteredSlots.includes(formData.time)) {
      setFormData(prev => ({ ...prev, time: '' }));
    }
  }, [date, formData.time]); // Add formData.time to dependencies if needed, but primarily driven by date

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const selectedDate = new Date(date);
    if (selectedDate.getDay() === 0 || selectedDate.getDay() === 6) {
       toast.error('Hétvégén nem fogadunk asztalfoglalást.');
       return;
    }

    if (!date || !formData.name || !formData.phone || !formData.guests || !formData.time) {
      toast.error('Kérjük, töltse ki az összes kötelező mezőt!');
      return;
    }

    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const payload = {
        name: formData.name,
        phone: formData.phone,
        guests: formData.guests,
        time: formData.time,
        date: date, 
      };
      
      if (formData.email && formData.email.trim() !== '') {
        payload.email = formData.email;
      }
      
      const response = await fetch(`${backendUrl}/api/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Hiba történt a foglalás során');
      }

      toast.success('Asztalfoglalás sikeresen elküldve! Hamarosan felvesszük Önnel a kapcsolatot.');
      
      setDate('');
      setFormData({
        name: '',
        phone: '',
        email: '',
        guests: '',
        time: ''
      });
      
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Sajnos nem sikerült elküldeni a foglalást. Kérjük, próbálja újra később.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTimeChange = (value) => {
    setFormData({
      ...formData,
      time: value
    });
  };

  const handleDateChange = (e) => {
    const selectedDateStr = e.target.value;
    
    if (!selectedDateStr) {
       setDate('');
       return;
    }

    const newDate = new Date(selectedDateStr);
    const day = newDate.getDay();

    if (day === 0 || day === 6) {
      toast.error('Hétvégére (Szombat, Vasárnap) nem lehet asztalt foglalni.');
      setDate('');
      return;
    }

    setDate(selectedDateStr);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] md:max-w-md max-h-[90vh] overflow-y-auto bg-card border-border text-card-foreground rounded-none p-4 md:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl md:text-2xl font-serif text-primary text-center md:text-left break-words">Asztalfoglalás</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name" className="text-foreground">Név *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Az Ön neve"
              required
              className="bg-background border-input text-foreground rounded-none text-base md:text-sm"
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-foreground">Telefonszám *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+36 70 123 4567"
              required
              className="bg-background border-input text-foreground rounded-none text-base md:text-sm"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-foreground">Email cím</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@pelda.hu"
              className="bg-background border-input text-foreground rounded-none text-base md:text-sm"
            />
          </div>

          <div>
            <Label htmlFor="guests" className="text-foreground">Vendégek száma *</Label>
            <Input
              id="guests"
              name="guests"
              type="number"
              min="1"
              max="20"
              value={formData.guests}
              onChange={handleChange}
              placeholder="2"
              required
              className="bg-background border-input text-foreground rounded-none text-base md:text-sm"
            />
          </div>

          <div>
            <Label htmlFor="date" className="text-foreground">Dátum (Hétköznapok) *</Label>
            <Input
              id="date"
              name="date"
              type="date"
              min={today}
              value={date}
              onChange={handleDateChange}
              required
              className="bg-background border-input text-foreground rounded-none cursor-pointer date-input-custom text-base md:text-sm"
            />
            <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">Hétvégére nem fogadunk asztalfoglalást.</p>
          </div>

          <div>
            <Label htmlFor="time" className="text-foreground">Időpont *</Label>
            <Select onValueChange={handleTimeChange} value={formData.time} disabled={!date || availableTimeSlots.length === 0}>
              <SelectTrigger className="w-full bg-background border-input text-foreground rounded-none text-base md:text-sm">
                <SelectValue placeholder={!date ? "Előbb válasszon dátumot" : "Válasszon időpontot"} />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground border-border max-h-[200px] z-[70] rounded-none">
                {availableTimeSlots.map((time) => (
                  <SelectItem key={time} value={time} className="hover:bg-accent hover:text-accent-foreground cursor-pointer font-sans">
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {date && availableTimeSlots.length > 0 && (
               <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">Hétköznap legkésőbb 15:30-ig.</p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-none uppercase tracking-widest text-xs h-12 mt-6"
            disabled={loading || !date || !formData.time}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {loading ? 'Küldés...' : 'Foglalás küldése'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
